import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Tooltip, CircularProgress } from '@material-ui/core';
import DescriptionIcon from '@material-ui/icons/Description';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Matches `GeoJsonExportButton`, the
 * labelled export beside it, so the bar stays one design.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:Core
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  button: {
    marginRight: theme.spacing(2),
    whiteSpace: 'nowrap',
  },
}));

/**
 * @function PdfReportButton
 * @description Writes the whole analysis to a single PDF: the area, the imagery, the model and its
 * calibration, the metrics, the error by depth band and the maps.
 * Labelled "PDF" for the same reason the GeoJSON button carries its format name - a third download
 * arrow in a row of download arrows says nothing about which is which - and a document icon rather
 * than an arrow, because what separates this one from its neighbours is not that it downloads but
 * that what comes down is meant to be read by a person rather than loaded by a program.
 * The format name is written here and not in the language files: PDF is spelled the same in every
 * language, so a key for it could only ever hold one value. The tooltip is prose and stays
 * translated.
 * Building the report asks Earth Engine for the map images, which takes a few seconds, so the
 * button shows its own progress and refuses a second click while it works.
 * Shown only while a result page has registered a builder - see `useRegisterPdfExport`.
 * @param {Object} props
 * @param {Function} props.onExport - Called on click; builds and downloads the PDF.
 * @see {@link useRegisterPdfExport}
 * **Called by:
 * @see {NavigationBar}
 * @memberof module:Core
 * @returns {JSX.Element}
 */
const PdfReportButton = ({ onExport }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const [building, setBuilding] = useState(false);

  const handleClick = async () => {
    if (building) return;
    setBuilding(true);
    try {
      await onExport();
    } finally {
      setBuilding(false);
    }
  };

  return (
    <Tooltip title={t('forms.acquisition.pdfReport.tooltip')}>
      <span>
        <Button
          className={classes.button}
          variant="outlined"
          color="primary"
          disabled={building}
          startIcon={building ? <CircularProgress size={16} color="inherit" /> : <DescriptionIcon />}
          onClick={handleClick}>
          PDF
        </Button>
      </span>
    </Tooltip>
  );
};

export default PdfReportButton;
